(() => {
  const GLOBAL_KEY = '__UV_CART_DRAWER_UPSELL_V1__';
  const VERSION = '1.1.3';
  const previous = window[GLOBAL_KEY];

  if (previous?.version === VERSION) {
    previous.scan();
    return;
  }

  // If this asset is hot-reloaded in the Theme Editor, neutralize an older
  // manager so its already-registered event listeners become harmless.
  if (previous) {
    previous.drawerObserver?.disconnect?.();
    for (const instance of previous.instances?.values?.() || []) {
      instance.sentinelObserver?.disconnect?.();
      instance.refreshController?.abort?.();
      instance.cartSettleObserver?.disconnect?.();
    }
    previous.instances?.clear?.();
  }

  class UpsellInstance {
    constructor(template) {
      this.template = template;
      this.sectionId = template.dataset.sectionId || '';
      this.targetSelector = template.dataset.targetSelector || '.cart-drawer__items';
      this.placement = template.dataset.placement || 'append';
      this.enableOverride = template.dataset.enableOverride === 'true';
      this.navigationMode = template.dataset.navigationMode || 'infinite_scroll';
      this.hideItemsInCart = template.dataset.hideItemsInCart === 'true';
      this.defaultFetchUrl = template.dataset.defaultFetchUrl || '/cart';
      this.rootUrl = template.dataset.rootUrl || '/';
      this.currentProductId = (template.dataset.currentProductId || '').trim();

      this.activeFetchBase = this.resolveActiveFetchBase();
      this.currentPage = 1;
      this.paginationLoading = false;
      this.fullRefreshVersion = 0;
      this.gridGeneration = 0;
      this.sentinelObserver = null;
      this.refreshController = null;
      this.cartSettleObserver = null;
      this.cartUpdating = false;
      this.cartUpdateToken = 0;
      this.lastOverrideHandle = this.getOverrideHandle();
    }

    destroy({ removeWidget = true } = {}) {
      this.sentinelObserver?.disconnect();
      this.sentinelObserver = null;
      this.refreshController?.abort();
      this.refreshController = null;
      this.cartSettleObserver?.disconnect();
      this.cartSettleObserver = null;
      this.cartUpdating = false;
      this.cartUpdateToken += 1;

      if (removeWidget) this.getWidget()?.remove();
      this.clearEmptyCartScrollFix();
    }

    getWidget() {
      const selector = `.uv-cdu-widget[data-uv-cdu-section-id="${CSS.escape(this.sectionId)}"]`;
      for (const widget of document.querySelectorAll(selector)) {
        if (!widget.closest('[data-uv-cdu-template]')) return widget;
      }
      return null;
    }

    getTarget() {
      try {
        return document.querySelector(this.targetSelector);
      } catch (error) {
        console.warn('[UV Cart Drawer Upsell] Invalid target selector:', this.targetSelector, error);
        return null;
      }
    }

    getOverrideHandle() {
      if (!this.enableOverride) return '';
      const configs = document.querySelectorAll('[data-uv-cdu-override-config]');
      for (let index = configs.length - 1; index >= 0; index -= 1) {
        const config = configs[index];
        if (!config.isConnected) continue;
        return (config.dataset.uvCduCollectionHandle || '').trim();
      }
      return '';
    }

    resolveActiveFetchBase() {
      const handle = this.getOverrideHandle();
      let baseUrl = this.defaultFetchUrl;

      if (handle) {
        const root = this.rootUrl.endsWith('/') ? this.rootUrl : `${this.rootUrl}/`;
        baseUrl = `${root}collections/${encodeURIComponent(handle)}`;
      }

      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}section_id=${encodeURIComponent(this.sectionId)}`;
    }

    buildDefaultFetchBase() {
      const separator = this.defaultFetchUrl.includes('?') ? '&' : '?';
      return `${this.defaultFetchUrl}${separator}section_id=${encodeURIComponent(this.sectionId)}`;
    }

    parseWidget(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector(`.uv-cdu-widget[data-uv-cdu-section-id="${CSS.escape(this.sectionId)}"]`)
        || doc.querySelector('.uv-cdu-widget[data-uv-cdu-widget]');
    }

    removeCurrentProductCards(root) {
      if (!root || !this.currentProductId) return;
      const selector = `.uv-cdu-card[data-uv-cdu-product-id="${CSS.escape(this.currentProductId)}"]`;
      root.querySelectorAll(selector).forEach((card) => card.remove());
    }

    clearEmptyCartScrollFix() {
      document.querySelectorAll('.uv-cdu-empty-scroll-owner').forEach((el) => el.classList.remove('uv-cdu-empty-scroll-owner'));
      document.querySelectorAll('.uv-cdu-empty-items-flow').forEach((el) => el.classList.remove('uv-cdu-empty-items-flow'));
    }

    applyEmptyCartScrollFix(widget = this.getWidget()) {
      this.clearEmptyCartScrollFix();
      if (!widget?.isConnected) return;

      const drawerComponent = widget.closest('cart-drawer-component');
      const emptyByClass = Boolean(widget.closest('.cart-drawer--empty'));
      const emptyByMarkup = Boolean(drawerComponent && !drawerComponent.querySelector('.cart-form'));
      if (!emptyByClass && !emptyByMarkup) return;

      const content = widget.closest('.cart-drawer__content');
      const items = widget.closest('.cart-drawer__items');
      content?.classList.add('uv-cdu-empty-scroll-owner');
      items?.classList.add('uv-cdu-empty-items-flow');

      requestAnimationFrame(() => {
        if (content && content.scrollTop < 0) content.scrollTop = 0;
      });
    }

    getScroller(widget = this.getWidget()) {
      if (!widget) return null;
      const content = widget.closest('.cart-drawer__content');
      const items = widget.closest('.cart-drawer__items');
      const drawerComponent = widget.closest('cart-drawer-component');
      const isEmpty = Boolean(widget.closest('.cart-drawer--empty')) || Boolean(drawerComponent && !drawerComponent.querySelector('.cart-form'));
      return (isEmpty ? content : items) || content || items;
    }

    scrollDrawerToTop(widget = this.getWidget()) {
      const scroller = this.getScroller(widget);
      if (!scroller) return;
      scroller.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      requestAnimationFrame(() => {
        if (scroller.isConnected && scroller.scrollTop !== 0) scroller.scrollTop = 0;
      });
    }

    ensureInjected({ hydrateOverride = true } = {}) {
      const existing = this.getWidget();
      if (existing) {
        this.applyEmptyCartScrollFix(existing);
        return existing;
      }

      const target = this.getTarget();
      const sourceWidget = this.template?.firstElementChild;
      if (!target || !sourceWidget) return null;

      const clone = sourceWidget.cloneNode(true);
      clone.removeAttribute('hidden');
      this.removeCurrentProductCards(clone);

      const desiredFetchBase = this.resolveActiveFetchBase();
      const needsOverrideHydration = desiredFetchBase !== this.buildDefaultFetchBase();
      this.activeFetchBase = desiredFetchBase;
      this.currentPage = 1;

      if (needsOverrideHydration && hydrateOverride) {
        clone.hidden = true;
        clone.dataset.uvCduHydrating = 'true';
      }

      switch (this.placement) {
        case 'prepend': target.prepend(clone); break;
        case 'before': target.before(clone); break;
        case 'after': target.after(clone); break;
        default: target.append(clone);
      }

      this.applyEmptyCartScrollFix(clone);

      if (needsOverrideHydration && hydrateOverride) {
        this.refreshGrid({ widget: clone, revealAfter: true, removeOnError: true });
      } else {
        this.observeSentinel();
      }

      return clone;
    }

    observeSentinel() {
      this.sentinelObserver?.disconnect();
      this.sentinelObserver = null;
      if (this.navigationMode !== 'infinite_scroll') return;

      const widget = this.getWidget();
      if (!widget) return;

      this.applyEmptyCartScrollFix(widget);
      const sentinel = widget.querySelector('[data-uv-cdu-sentinel]');
      if (!sentinel) return;

      const root = this.getScroller(widget);
      this.sentinelObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !this.paginationLoading) {
            this.loadNextPage();
            break;
          }
        }
      }, {
        root: root instanceof Element ? root : null,
        rootMargin: '200px 0px',
        threshold: 0,
      });

      this.sentinelObserver.observe(sentinel);
    }

    async fetchPage(base, page, signal) {
      const separator = base.includes('?') ? '&' : '?';
      const response = await fetch(`${base}${separator}page=${page}`, {
        credentials: 'same-origin',
        cache: 'no-store',
        signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    }

    async refreshGrid({ widget = this.getWidget(), revealAfter = false, removeOnError = false } = {}) {
      if (!widget?.isConnected) return;

      const version = ++this.fullRefreshVersion;
      const fetchBase = this.resolveActiveFetchBase();
      const wasHydrating = widget.dataset.uvCduHydrating === 'true';
      const shouldReveal = revealAfter || wasHydrating;
      const shouldRemoveOnError = removeOnError || wasHydrating;

      this.activeFetchBase = fetchBase;
      this.lastOverrideHandle = this.getOverrideHandle();
      this.currentPage = 1;
      this.gridGeneration += 1;
      this.paginationLoading = false;
      this.sentinelObserver?.disconnect();

      this.refreshController?.abort();
      const controller = new AbortController();
      this.refreshController = controller;

      try {
        const html = await this.fetchPage(fetchBase, 1, controller.signal);
        if (version !== this.fullRefreshVersion || !widget.isConnected) return;

        const fresh = this.parseWidget(html);
        if (!fresh) throw new Error('Upsell widget missing from Section Rendering response');
        this.removeCurrentProductCards(fresh);

        widget.innerHTML = fresh.innerHTML;
        if (shouldReveal) {
          widget.hidden = false;
          delete widget.dataset.uvCduHydrating;
        }

        this.applyEmptyCartScrollFix(widget);
        this.observeSentinel();
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.warn('[UV Cart Drawer Upsell] Grid refresh failed:', error);
        if (shouldRemoveOnError && widget.isConnected) widget.remove();
      } finally {
        if (this.refreshController === controller) this.refreshController = null;
      }
    }

    async loadNextPage() {
      if (this.navigationMode !== 'infinite_scroll' || this.paginationLoading) return;

      const widget = this.getWidget();
      if (!widget) return;

      this.paginationLoading = true;
      const generation = this.gridGeneration;
      const base = this.activeFetchBase;
      const nextPage = this.currentPage + 1;

      try {
        const html = await this.fetchPage(base, nextPage);
        if (generation !== this.gridGeneration || !widget.isConnected) return;

        const fresh = this.parseWidget(html);
        if (!fresh) return;
        this.removeCurrentProductCards(fresh);

        const liveGrid = widget.querySelector('[data-uv-cdu-grid]');
        const freshGrid = fresh.querySelector('[data-uv-cdu-grid]');
        if (liveGrid && freshGrid) {
          for (const card of Array.from(freshGrid.children)) liveGrid.appendChild(card);
        }

        const liveFooter = widget.querySelector('[data-uv-cdu-footer]');
        const freshFooter = fresh.querySelector('[data-uv-cdu-footer]');
        if (liveFooter && freshFooter) liveFooter.innerHTML = freshFooter.innerHTML;

        this.currentPage = nextPage;
        this.observeSentinel();
      } catch (error) {
        console.warn('[UV Cart Drawer Upsell] Infinite-scroll page failed:', error);
      } finally {
        if (generation === this.gridGeneration) this.paginationLoading = false;
      }
    }

    async loadPage(pageNumber) {
      if (this.navigationMode !== 'pagination' || this.paginationLoading) return;

      const requestedPage = Number(pageNumber);
      const widget = this.getWidget();
      if (!widget || !Number.isInteger(requestedPage) || requestedPage < 1 || requestedPage === this.currentPage) return;

      this.paginationLoading = true;
      const generation = this.gridGeneration;
      const base = this.activeFetchBase;
      widget.setAttribute('aria-busy', 'true');

      try {
        const html = await this.fetchPage(base, requestedPage);
        if (generation !== this.gridGeneration || !widget.isConnected) return;

        const fresh = this.parseWidget(html);
        if (!fresh) throw new Error('Upsell widget missing from pagination response');
        this.removeCurrentProductCards(fresh);

        const liveGrid = widget.querySelector('[data-uv-cdu-grid]');
        const freshGrid = fresh.querySelector('[data-uv-cdu-grid]');
        const liveFooter = widget.querySelector('[data-uv-cdu-footer]');
        const freshFooter = fresh.querySelector('[data-uv-cdu-footer]');

        if (liveGrid && freshGrid) liveGrid.replaceWith(freshGrid);
        if (liveFooter && freshFooter) liveFooter.replaceWith(freshFooter);

        this.currentPage = requestedPage;
        this.applyEmptyCartScrollFix(widget);
      } catch (error) {
        console.warn('[UV Cart Drawer Upsell] Pagination failed:', error);
      } finally {
        if (generation === this.gridGeneration) this.paginationLoading = false;
        if (widget.isConnected) widget.removeAttribute('aria-busy');
      }
    }

    getDrawerCartItemsComponent() {
      return document.querySelector('#cart-drawer cart-items-component');
    }

    cartDomMatches(items) {
      if (!Array.isArray(items)) return false;

      const component = this.getDrawerCartItemsComponent();
      if (!component) {
        return items.length === 0 && Boolean(document.querySelector('#cart-drawer .cart-drawer--empty'));
      }

      const rows = Array.from(component.querySelectorAll('tr[ref="cartItemRows[]"][data-key]'));
      if (rows.length !== items.length) return false;

      const expected = new Map(items.map((item) => [String(item.key || ''), Number(item.quantity)]));
      for (const row of rows) {
        const key = row.dataset.key || '';
        if (!expected.has(key)) return false;

        const input = row.querySelector('input[name="updates[]"]');
        if (input && Number(input.value) !== expected.get(key)) return false;
      }

      return true;
    }

    waitForCartDom(items, token) {
      if (token !== this.cartUpdateToken) return Promise.resolve(false);
      if (this.cartDomMatches(items)) return Promise.resolve(true);

      this.cartSettleObserver?.disconnect();
      this.cartSettleObserver = null;

      const root = document.getElementById('cart-drawer');
      if (!root) return Promise.resolve(false);

      return new Promise((resolve) => {
        let settled = false;
        const finish = (matched) => {
          if (settled) return;
          settled = true;
          observer.disconnect();
          clearTimeout(timeoutId);
          if (this.cartSettleObserver === observer) this.cartSettleObserver = null;
          resolve(matched);
        };

        const check = () => {
          if (token !== this.cartUpdateToken) return finish(false);
          if (this.cartDomMatches(items)) finish(true);
        };

        const observer = new MutationObserver(check);
        observer.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['value', 'data-key'],
        });
        this.cartSettleObserver = observer;

        // Fallback only: normal Horizon 4.1.5 updates resolve via the observer as
        // soon as the returned cart rows/quantities are present.
        const timeoutId = setTimeout(() => finish(this.cartDomMatches(items)), 1200);
        check();
      });
    }

    async onCartEvent(event) {
      const token = ++this.cartUpdateToken;
      const action = event?.action || 'update';
      this.cartUpdating = true;

      try {
        const result = event?.promise && typeof event.promise.then === 'function'
          ? await event.promise
          : null;

        if (token !== this.cartUpdateToken) return;
        if (result?.detail?.didError) return;

        const items = result?.detail?.items;
        await this.waitForCartDom(items, token);
        if (token !== this.cartUpdateToken) return;

        this.cartUpdating = false;
        const widget = this.ensureInjected({ hydrateOverride: false });
        if (!widget) return;

        // Quantity-only updates don't change which products should be hidden.
        // Only add/remove can change product membership in the upsell grid.
        if (this.hideItemsInCart && (action === 'add' || action === 'remove')) {
          await this.refreshGrid({ widget });
        } else {
          this.applyEmptyCartScrollFix(widget);
          this.observeSentinel();
        }
      } catch (error) {
        if (token !== this.cartUpdateToken) return;
        if (error?.name !== 'AbortError') console.warn('[UV Cart Drawer Upsell] Cart update promise rejected:', error);
      } finally {
        if (token === this.cartUpdateToken) {
          this.cartUpdating = false;
          this.ensureInjected({ hydrateOverride: false });
        }
      }
    }

    syncOverrideIfChanged() {
      const handle = this.getOverrideHandle();
      const desiredBase = this.resolveActiveFetchBase();
      const handleChanged = handle !== this.lastOverrideHandle;
      const baseChanged = desiredBase !== this.activeFetchBase;
      if (!handleChanged && !baseChanged) return;

      this.lastOverrideHandle = handle;
      const widget = this.ensureInjected({ hydrateOverride: false });
      if (!widget) return;

      const needsHydration = desiredBase !== this.buildDefaultFetchBase();
      if (needsHydration) {
        widget.hidden = true;
        widget.dataset.uvCduHydrating = 'true';
      }
      this.refreshGrid({ widget, revealAfter: needsHydration, removeOnError: needsHydration });
    }
  }

  const manager = {
    version: VERSION,
    instances: new Map(),
    drawerObserver: null,

    scan() {
      const liveTemplates = new Map();
      document.querySelectorAll('[data-uv-cdu-template]').forEach((template) => {
        const id = template.dataset.sectionId;
        if (id) liveTemplates.set(id, template);
      });

      for (const [id, instance] of this.instances) {
        const template = liveTemplates.get(id);
        if (!template || !template.isConnected || instance.template !== template) {
          instance.destroy({ removeWidget: true });
          this.instances.delete(id);
        }
      }

      for (const [id, template] of liveTemplates) {
        if (!this.instances.has(id)) this.instances.set(id, new UpsellInstance(template));
        const instance = this.instances.get(id);
        instance.ensureInjected();
        instance.syncOverrideIfChanged();
      }

      this.observeDrawer();
    },

    observeDrawer() {
      const drawer = document.getElementById('cart-drawer');
      if (!drawer || this.drawerObserver?.__uvRoot === drawer) return;

      this.drawerObserver?.disconnect();
      const observer = new MutationObserver(() => {
        for (const instance of this.instances.values()) {
          // Never inject into the middle of Horizon's section morph. onCartEvent
          // reinjects after the live cart rows/quantities match the returned cart.
          if (instance.cartUpdating) continue;
          if (!instance.getWidget() && instance.getTarget()) instance.ensureInjected();
        }
      });
      observer.observe(drawer, { childList: true, subtree: true });
      observer.__uvRoot = drawer;
      this.drawerObserver = observer;
    },
  };

  window[GLOBAL_KEY] = manager;

  const onReady = () => manager.scan();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady, { once: true });
  else onReady();

  document.addEventListener('shopify:cart:lines-update', (event) => {
    for (const instance of manager.instances.values()) instance.onCartEvent(event);
  });

  document.addEventListener('shopify:cart:view', () => {
    for (const instance of manager.instances.values()) instance.ensureInjected();
  });

  // Compatibility for custom cart code that emits legacy events. No fetch patch.
  // We don't refetch here because these events don't provide reliable action/cart
  // metadata; the targeted drawer observer restores the widget if Horizon replaces it.
  document.addEventListener('cart:updated', () => manager.scan());
  document.addEventListener('cart:add', () => manager.scan());

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const pageButton = event.target.closest('[data-uv-cdu-page]');
    if (pageButton && !pageButton.disabled) {
      const widget = pageButton.closest('[data-uv-cdu-widget]');
      const sectionId = widget?.dataset.uvCduSectionId;
      const instance = sectionId ? manager.instances.get(sectionId) : null;
      if (instance) {
        event.preventDefault();
        instance.scrollDrawerToTop(widget);
        instance.loadPage(pageButton.dataset.uvCduPage);
        return;
      }
    }

    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const card = event.target.closest('a.uv-cdu-card');
    if (!card || card.closest('[data-uv-cdu-template]') || (card.getAttribute('target') || '').toLowerCase() === '_blank') return;
    card.classList.add('uv-cdu-product-loading');
    card.setAttribute('aria-busy', 'true');
  }, true);

  window.addEventListener('pageshow', () => {
    document.querySelectorAll('.uv-cdu-card.uv-cdu-product-loading').forEach((card) => {
      card.classList.remove('uv-cdu-product-loading');
      card.removeAttribute('aria-busy');
    });
    manager.scan();
  });

  document.addEventListener('shopify:section:load', () => manager.scan());
  document.addEventListener('shopify:section:unload', () => manager.scan());
})();
